import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '@bnnvara/accounts';
import PropTypes from 'prop-types';
import React, { useEffect, useReducer } from 'react';

import { Bookmark } from '../../api/mutations/bookmark.gql';
import { Bookmarked, BookmarkedIds } from '../../api/queries/me/bookmarked.gql';
import withApolloClient from '../../hocs/withApolloClient';
import { BookmarkContext } from './BookmarkContext';
import { getIsBookmarkedByUser } from './getIsBookmarkedByUser';
import { reducer } from './reducer';

function BookmarkProviderInner({ children }) {
  /**
   * We have structured the bookmarks by:
   * 1.) by storing your bookmarked ids => this is used to check whether you have bookmarked something if you go to a article page
   * 2.) by storing the ids of a bookmark + als the variables that are needed to display them as data  => this is used if you go to the page where the bookmarks are displayed as cards.
   * The reason for doing this sepereately is because only retrieving the id's is significantly faster,
   * and thus it doesn't take so long before you can bookmark article if you visit a article page.
   */
  const initialState = {
    ids: [],
    mixedContent: [],
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    profile: { UID },
  } = useAccount();

  const [toggleBookmark] = useMutation(Bookmark);

  /**
   * Retrieves the id's of all bookmarked publications
   */
  const { data: dataBookmarkedIds, loading: loadingBookmarkedIds } = useQuery(
    BookmarkedIds,
    {
      variables: {
        userId: UID,
      },
      skip: !UID,
      fetchPolicy: 'network-only',
    }
  );

  /**
   * Sets the retrieved ids in a state
   */
  useEffect(() => {
    if (!dataBookmarkedIds?.me?.bookmarkedIds?.ids) {
      return;
    }

    dispatch({
      type: 'SET_IDS',
      payload: dataBookmarkedIds?.me?.bookmarkedIds?.ids,
    });
  }, [dataBookmarkedIds]);

  /**
   * Retrieve the ids of the mixedcontent + all its values to display them as cards
   */
  const {
    data: dataBookmarkedMixedContent,
    loading: loadingBookmarkedMixedContent,
  } = useQuery(Bookmarked, {
    variables: {
      userId: UID,
    },
    skip: !UID,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!dataBookmarkedMixedContent || loadingBookmarkedMixedContent) {
      return;
    }

    dispatch({
      type: 'SET_MIXED_CONTENT',
      payload: dataBookmarkedMixedContent?.me?.bookmarked?.success,
    });
  }, [dataBookmarkedMixedContent, loadingBookmarkedMixedContent]);

  const value = {
    ...state,
    loadingBookmarkedMixedContent,
    getIsBookmarkedByUser,
    async toggleBookmark(content) {
      if (loadingBookmarkedIds) return null;
      const oldValue = getIsBookmarkedByUser(state.ids, content.id);
      const newValue = !oldValue;

      const result = await toggleBookmark({
        variables: {
          contentId: content.id,
          contentType: content.type,
          userId: UID,
          isBookmarked: newValue,
        },
      });

      if (result.data.bookmark.error !== undefined) {
        return null;
      }
      if (result.data.bookmark.isBookmarked) {
        dispatch({
          type: 'ADD',
          payload: content,
        });
      } else {
        dispatch({
          type: 'REMOVE',
          payload: content,
        });
      }
      return result.data.bookmark.isBookmarked;
    },
  };

  return (
    <BookmarkContext.Provider value={value}>
      {children}
    </BookmarkContext.Provider>
  );
}

BookmarkProviderInner.propTypes = {
  children: PropTypes.node,
};

export const BookmarkProvider = withApolloClient(BookmarkProviderInner);
